import { supabase } from './supabaseClient';

// g is a GeoJSON Polygon *geometry* object: { type:'Polygon', coordinates:[...] }
export async function createBuilding(geometry) {
  const { data, error } = await supabase.rpc('create_building', { g: geometry });
  return { id: data, error };
}

export async function updateBuilding(id, geometry) {
  const { error } = await supabase.rpc('update_building', { b_id: id, g: geometry });
  return { error };
}

export async function deleteBuilding(id) {
  const { error } = await supabase.from('buildings').delete().eq('id', id);
  return { error };
}

// Uses the jsonb_agg loader (buildings_in_bbox_json): a row-returning RPC is
// capped at PostgREST's project-wide max-rows=1000, which silently truncated
// dense viewports; one jsonb row is not capped, so a viewport returns ALL its
// buildings (up to the function's inner safety LIMIT). Same row shape as before.
export async function buildingsInBbox(bounds) {
  const { data, error } = await supabase.rpc('buildings_in_bbox_json', {
    min_lng: bounds.west, min_lat: bounds.south,
    max_lng: bounds.east, max_lat: bounds.north,
  });
  return { rows: Array.isArray(data) ? data : [], error };
}

// Complete loader for workflows that must make a correct decision about every
// building in an area. A response of exactly 6001 rows is the SQL function's
// truncation signal, so replace that tile with four recursively loaded children.
// Quadrant borders can return the same polygon more than once; ids make the
// final union stable and unique. The map deliberately keeps using the bounded
// buildingsInBbox() call above.
export async function buildingsInBboxComplete(bounds) {
  const CAP = 6001;
  const MIN_TILE_DEGREES = 1e-5;
  let tiles = 0;
  let saturated = false;
  let firstError = null;

  const loadTile = async (tile) => {
    tiles += 1;
    let response;
    try { response = await buildingsInBbox(tile); }
    catch (error) {
      if (!firstError) firstError = error;
      return [];
    }

    const { rows, error } = response;
    if (error) {
      if (!firstError) firstError = error;
      return [];
    }
    if (rows.length !== CAP) return rows;

    const width = tile.east - tile.west;
    const height = tile.north - tile.south;
    if (width <= MIN_TILE_DEGREES || height <= MIN_TILE_DEGREES) {
      saturated = true;
      return rows;
    }

    const midLng = tile.west + (width / 2);
    const midLat = tile.south + (height / 2);
    const quadrants = [
      { west: tile.west, south: tile.south, east: midLng, north: midLat },
      { west: midLng, south: tile.south, east: tile.east, north: midLat },
      { west: tile.west, south: midLat, east: midLng, north: tile.north },
      { west: midLng, south: midLat, east: tile.east, north: tile.north },
    ];
    const childRows = await Promise.all(quadrants.map(loadTile));
    return childRows.flat();
  };

  const loaded = await loadTile(bounds);
  const byId = new Map();
  loaded.forEach((row) => {
    if (row && !byId.has(row.id)) byId.set(row.id, row);
  });
  return {
    rows: Array.from(byId.values()), tiles, saturated, error: firstError,
  };
}

// Cheap density probe: how many active buildings are in the viewport, capped at
// 6001 server-side (the scan stops there). The map uses this to decide between
// the editable Polygon layer (<= cap) and the raster footprint overlay (> cap)
// WITHOUT fetching up to 6001 full rows just to count them. Returns a number;
// 0 on error so the caller falls back to the (gated) Polygon path.
export async function buildingsCountInBbox(bounds) {
  const { data, error } = await supabase.rpc('buildings_count_in_bbox', {
    min_lng: bounds.west, min_lat: bounds.south,
    max_lng: bounds.east, max_lat: bounds.north,
  });
  return { count: typeof data === 'number' ? data : 0, error };
}

// Design-page loader: same footprints but with the demand attributes
// (floor_count / unit_count / unit_source / has_entry) the detailed-mode hover
// shows. Separate RPC so the annotation map's buildings_in_bbox stays untouched.
export async function designBuildingsInBbox(bounds) {
  const { data, error } = await supabase.rpc('design_buildings_in_bbox', {
    min_lng: bounds.west, min_lat: bounds.south,
    max_lng: bounds.east, max_lat: bounds.north,
  });
  return { rows: data || [], error };
}

// An operator flags another operator's problematic polygon. reporter_id is
// pinned to the caller server-side; reason is free text.
export async function reportBuilding(id, reason) {
  const { data, error } = await supabase.rpc('report_building', { b_id: id, reason });
  return { id: data, error };
}

// Admin review queue: every report joined with the offending building's owner
// and the reporter. Non-admins get an empty list (server-enforced).
export async function listBuildingReports() {
  const { data, error } = await supabase.rpc('list_building_reports');
  return { rows: data || [], error };
}

// Admin-only: flip a report between 'open' and 'resolved' without touching the
// building. The set_report_status RPC relies on the admins-only update RLS
// policy as its gate, so a non-admin caller gets a null id back (no-op).
export async function setReportStatus(reportId, status) {
  const { data, error } = await supabase.rpc('set_report_status', {
    r_id: reportId, new_status: status,
  });
  return { id: data, error };
}

// Admin-only: permanently delete an operator. The auth.users delete cascades
// (profiles.id FK -> profiles, buildings.operator_id FK -> buildings), so the
// operator's login, profile, and every polygon they drew are removed in one
// atomic operation. The server (admin_delete_operator) enforces admin-only and
// refuses to delete yourself or another admin.
export async function deleteOperator(id) {
  const { data, error } = await supabase.rpc('admin_delete_operator', { target: id });
  return { deleted: data, error };
}

// Per-operator active-polygon tally for the admin "All Operators" tab.
// Aggregation happens server-side because PostgREST caps a plain
// .from('buildings').select('operator_id') at 1000 rows (the platform's
// db-max-rows). Once the project crossed 1000 active polygons every count
// silently truncated and any operator whose rows fell outside the first 1000
// of Postgres's unordered scan disappeared from the admin list. The RPC
// returns one row per operator (~10s of rows), so the cap never applies.
export async function operatorBuildingCounts(signal) {
  const request = supabase.rpc('operator_building_counts');
  const { data, error } = await (signal ? request.abortSignal(signal) : request);
  if (error) return { counts: {}, error };
  const counts = {};
  (data || []).forEach((row) => {
    counts[row.operator_id] = Number(row.count);
  });
  return { counts, error: null };
}
